import {Controller} from "@hotwired/stimulus";
import {Turbo} from "@hotwired/turbo-rails";

/**
 * Expose APIs to connect with a form
 */
export default class FormController extends Controller {
  static values = {
    // Submit the form when the element is connected to the DOM
    submitOnLoad: Boolean,
    // Update the Browser's URL with the params
    syncUrlParams: Boolean,
    /**
    * Offers a way to sync fields with another form
    * @example 
    * [{ form: "filter_by_gym_form", fieldsToSync: ["gym_id"] }]
    */
    syncFormFields: Array,
    disableAllFieldsOnSubmit: Boolean,
  };

  static targets = ['loading'];

  syncedFormListeners = [];

  connect() {
    console.debug("FORM connected", this.element, this.disableAllFieldsOnSubmitValue);

    this.syncedFormListeners = [];

    // This is not firing inside a Bootstrap modal
    this.element.addEventListener('turbo:before-fetch-request', (event) => {
      console.debug('  Before request', event);
      if (this.disableAllFieldsOnSubmitValue) {
        this.disableAllInputs();
      }

      if (this.syncUrlParamsValue) {
        this.syncParams(event.detail.url);
      }

      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.remove('d-none');
      }

    }, false);

    this.element.addEventListener('turbo:before-fetch-response', (event) => {
      console.debug('=> Before response', event);
      if (this.disableAllFieldsOnSubmitValue) {
        this.enableAllInputs();
      }

      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('d-none');
      }
    }, false);

    if (this.submitOnLoadValue) {
      setTimeout(() => {
        console.debug("=> Requesting submit")
        this.element.requestSubmit();
      });
    }

    if (this.hasSyncFormFieldsValue) {
      this.syncFormFields();
    }
  }

  disconnect() {
    console.debug("FORM Disconnected", this.disableAllFieldsOnSubmitValue);

    this.syncedFormListeners.forEach(formListener => {
      formListener.form.removeEventListener('change', formListener.listener);
    })
  }

  syncFormFields() {
    console.debug("FORM CONTROLLER: Syncing form", this.element.id, this.syncFormFieldsValue);
    console.warn("Please sync this form", this.element.id);

    this.syncFormFieldsValue.forEach(config => {
      const form = document.getElementById(config.form);

      if (form == null) {
        console.warn(`The form "${config.form}" is not defined`);
        return;
      }

      if (config.fieldsToSync == null) {
        console.warn(`fieldsToSync not defined in the config descriptor for ${config.form}`);
        return;
      }

      // Create inputs hidden values
      config.fieldsToSync.forEach(fieldToSync => {
        const hiddenInput = document.createElement('input');
        const value = form[fieldToSync] && form[fieldToSync].value;
        hiddenInput.type = 'hidden';
        hiddenInput.name = fieldToSync;
        hiddenInput.value = value == undefined ? '' : value;
        console.debug("=> Syncing", fieldToSync, value);
        this.element.appendChild(hiddenInput);
      });

      const listener = (event) => {
        console.debug("Form Input change", event, this);
        console.debug("Change", event.target.name, event.target.value);

        if (config.fieldsToSync.includes(event.target.name)) {
          const inputToUpdate = this.element[event.target.name];
          console.debug(`Found trackable field ${event.target.name}` , inputToUpdate);

          inputToUpdate.value = event.target.value;
        }
      }

      form.addEventListener('change', listener, false);

      this.syncedFormListeners.push({ form: form, listener: listener });
    });
  }


  syncParams(url) {
    console.debug('  Sync URL Params', url.searchParams, url.searchParams.toString());
    const newUrl = new URL(window.location.href);

    url.searchParams.forEach((value, key) => {
      if (key === 'commit') {
        return;
      }

      newUrl.searchParams.set(key, value);
    });

    window.history.pushState(history.state, '', newUrl);
  }

  disableAllInputs() {
    for (const element of this.element.elements) {
      element.setAttribute('disabled', 'disabled');
    }
  }

  enableAllInputs() {
    for (const element of this.element.elements) {
      element.removeAttribute('disabled');
    }
  }

}
